import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, FormControlLabel, Grid, Switch } from '@mui/material';

import { TextAreaField } from 'c-components';

import { useDebouncedCallback } from 'use-debounce';
import { useCommonTranslation } from 'c-translation';
import { fieldBackdropColor } from 'c-lib';
import DropdownFilter from '../EntityFilters/DropdownFilter';

type Props = {
    filters: any[];
    onApply: (filters: any) => void;
    initialFilterValues?: any;
    idFilterMode?: 'frame' | 'display';
    onIdFilterModeChange?: (mode: 'frame' | 'display') => void;
};

const StandaloneFilters: React.FC<Props> = ({
    filters,
    onApply,
    initialFilterValues,
    idFilterMode = 'frame',
    onIdFilterModeChange,
}) => {
    const [filterValues, setFilterValues] = useState(initialFilterValues);
    const [idFilterInputs, setIdFilterInputs] = useState(() => ({
        frame: ((initialFilterValues?.['filter.frame_id'] as string[]) || []).join('\n'),
        display: ((initialFilterValues?.['filter.id'] as string[]) || []).join('\n'),
    }));
    const t = useCommonTranslation();
    const activeIdFilterInput = useMemo(
        () => idFilterInputs[idFilterMode],
        [idFilterInputs, idFilterMode],
    );
    const activeIdFilterCount = useMemo(
        () => activeIdFilterInput.split('\n').filter(str => str.trim() !== '').length,
        [activeIdFilterInput],
    );

    const validFilters = useMemo(
        () =>
            filters
                .filter(filter => filter.expose === true)
                .map(filter => ({
                    ...filter,
                    options: Array.isArray(filter.options) ? filter.options : [],
                })),
        [filters],
    );

    const sortedOptions = (options = []) => {
        if (!Array.isArray(options)) return [];
        return [...options].sort((a, b) => (a.disabled === b.disabled ? 0 : a.disabled ? 1 : -1));
    };
    const handleFilterChange = useCallback((key, val) => {
        setFilterValues(prev => ({
            ...prev,
            [key]: val,
        }));
    }, []);

    useEffect(() => {
        setFilterValues(prev => ({
            ...prev,
            [`filter.${idFilterMode === 'display' ? 'id' : 'frame_id'}`]: activeIdFilterInput
                .split('\n')
                .map(id => id.trim())
                .filter(id => id),
            [`filter.${idFilterMode === 'display' ? 'frame_id' : 'id'}`]: [],
        }));
    }, [activeIdFilterInput, idFilterMode]);

    const debouncedApplyFilters = useDebouncedCallback(() => {
        onApply(filterValues);
    }, 500);
    useEffect(() => {
        debouncedApplyFilters();
    }, [filterValues, debouncedApplyFilters]);
    const allFilterComponents = useMemo(
        () => (
            <Grid container spacing={1} alignItems="center" wrap="wrap" pt={1}>
                {validFilters.map(filter => (
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={filter.key}>
                        <Box
                            sx={{
                                bgcolor: fieldBackdropColor,
                                borderRadius: '8px',
                            }}
                        >
                            <DropdownFilter
                                config={{
                                    ...filter,
                                    options: sortedOptions(filter.options).map(opt => ({
                                        label: opt.name,
                                        value: opt.value,
                                        disabled: opt.disabled ?? false,
                                    })),
                                }}
                                value={filterValues[filter.key] || []}
                                onChange={val => handleFilterChange(filter.key, val)}
                                id={`filter-${filter.key}`}
                                className={`filter-${filter.key}`}
                                count={filterValues[filter.key]?.length || 0}
                            />
                        </Box>
                    </Grid>
                ))}

                {filters.length > 0 && (
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                        <Box sx={{ width: '100%' }}>
                            <FormControlLabel
                                sx={{ mb: 1 }}
                                control={
                                    <Switch
                                        checked={idFilterMode === 'display'}
                                        onChange={event =>
                                            onIdFilterModeChange?.(
                                                event.target.checked ? 'display' : 'frame',
                                            )
                                        }
                                    />
                                }
                                label={
                                    idFilterMode === 'display'
                                        ? t(
                                              'Modules.Main.Campaigns.CampaignSchedule.displaysTab.displayIds',
                                          )
                                        : t(
                                              'Modules.Main.Campaigns.CampaignSchedule.displaysTab.frameIds',
                                          )
                                }
                            />
                            <TextAreaField
                                placeholder={t(
                                    idFilterMode === 'display'
                                        ? 'Modules.Main.Campaigns.CampaignSchedule.displaysTab.displayIdPlaceholder'
                                        : 'Modules.Main.Campaigns.CampaignSchedule.displaysTab.textAreaPlaceholder',
                                )}
                                multiline
                                fullWidth
                                rows={3}
                                value={activeIdFilterInput}
                                onChange={e =>
                                    setIdFilterInputs(prev => ({
                                        ...prev,
                                        [idFilterMode]: e.target.value,
                                    }))
                                }
                                variant="outlined"
                                helperText={`${t(
                                    idFilterMode === 'display'
                                        ? 'Modules.Main.Campaigns.CampaignSchedule.displaysTab.displayIds'
                                        : 'Modules.Main.Campaigns.CampaignSchedule.displaysTab.frameIds',
                                )}: ${activeIdFilterCount}`}
                                showCharacterCount={false}
                            />
                        </Box>
                    </Grid>
                )}
            </Grid>
        ),
        [
            validFilters,
            filters.length,
            t,
            activeIdFilterInput,
            activeIdFilterCount,
            filterValues,
            handleFilterChange,
            idFilterMode,
            onIdFilterModeChange,
        ],
    );
    return <>{allFilterComponents}</>;
};

export default StandaloneFilters;
